import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, CardActionArea, Skeleton, Fade } from '@mui/material';
import { Link } from 'react-router-dom';

// Component to render each show card with skeleton, fade-in, and linking to detail page
export default function ShowCard({ id, title, description, image, compact = false }) {
  const [imageLoaded, setImageLoaded] = useState(false); // Track when the image is loaded

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardActionArea sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch', minWidth: 0 }}>
        {/* Wrap the card content inside a Link to navigate to show detail using the show id */}
        <Link
          to={`/show/${id}`}
          style={{ textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}
        >
          {/* Flexible image area: fills whatever space the tile is given (regular or feature-sized) */}
          <Box sx={{ flex: '1 1 auto', minHeight: 160, position: 'relative', borderRadius: 1, overflow: 'hidden' }}>
            {!imageLoaded && (
              <Skeleton variant="rectangular" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
            )}
            <Fade in={imageLoaded} timeout={600}>
              <img
                src={image}
                alt={title}
                onLoad={() => setImageLoaded(true)}
                onError={(e) => {
                  e.target.onerror = null;
                }}
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: imageLoaded ? 'block' : 'none',
                }}
              />
            </Fade>
          </Box>

          <CardContent sx={{ flex: '0 0 auto', minWidth: 0 }}>
            {/* Title with skeleton */}
            {title ? (
              <Typography
                gutterBottom
                variant="h5"
                component="div"
                sx={{
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  ...(compact && { fontSize: '1rem' }),
                }}
              >
                {title}
              </Typography>
            ) : (
              <Skeleton variant="text" width="80%" /> // Show skeleton for title
            )}

            {/* Description with skeleton */}
            {description ? (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  display: '-webkit-box',
                  overflow: 'hidden',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 3,  // Limits the text to 3 lines
                  textOverflow: 'ellipsis',
                  ...(compact && { fontSize: '0.75rem' }),
                }}
              >
                {description || 'No description available.'}
              </Typography>
            ) : (
              <Skeleton variant="text" width="100%" /> // Show skeleton for description
            )}
          </CardContent>
        </Link>
      </CardActionArea>
    </Card>
  );
}
